import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetProjectQuery } from "fond/api/projectSlice";
import { useGetVersionScenarioConfigurationQuery, useGetVersionsQuery } from "fond/api/versionsSlice";
import { Maybe, VersionScenarioConfiguration } from "fond/types";
import { isCollaborationProject } from "fond/utils/project";

type UseGetScenarioConfigurationArgs = {
  projectId: Maybe<string>;
};

type UseGetScenarioConfigurationReturn = {
  configuration?: VersionScenarioConfiguration;
  hasConfig: boolean;
  hasGeneratedScenario: boolean;
  isLoading: boolean;
};

/**
 * Get the scenario configuration for a project.
 *
 * We use this hook because in a collaboration project, only the root version (the oldest one) has a
 * scenario configuration, so we need to get the versions and then get the scenario configuration
 * for the root version.
 */
export const useGetScenarioConfiguration = ({ projectId }: UseGetScenarioConfigurationArgs): UseGetScenarioConfigurationReturn => {
  const { currentData: project, isLoading: isLoadingProject } = useGetProjectQuery(projectId || skipToken);
  const isCollaboration = isCollaborationProject(project);
  // Scenarios are only supported on collaboration projects, so skip all the other queries if this isn't the case.
  const { currentData: versions, isLoading: isLoadingVersions } = useGetVersionsQuery(projectId ?? skipToken, { skip: !isCollaboration });
  const { currentData: configuration, isLoading: isLoadingCollaboration } = useGetVersionScenarioConfigurationQuery(
    // only the root (first) version in a collaboration project can have a scenario configuration. versions
    // are ordered from newest to oldest, so the root version is the last one in the list.
    versions && versions.ids?.length > 0 ? (versions.ids.at(-1) as string) : skipToken,
    { skip: !isCollaboration }
  );

  const isLoading = useMemo(() => {
    return isLoadingProject || isLoadingVersions || isLoadingCollaboration;
  }, [isLoadingProject, isLoadingVersions, isLoadingCollaboration]);

  return useMemo(() => {
    const hasConfig = !!configuration?.ID;
    return {
      configuration,
      hasConfig,
      hasGeneratedScenario: hasConfig && (versions?.ids?.length ?? 0) > 1,
      isLoading,
    };
  }, [configuration, isLoading, versions]);
};
